import {
  CheckCircleIcon,
  Cog6ToothIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { Button } from '@heroui/button';
import { Card, CardBody, CardHeader } from '@heroui/card';
import { Chip } from '@heroui/chip';
import { Divider } from '@heroui/divider';
import { Input } from '@heroui/input';
import { Switch } from '@heroui/switch';
import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import SEOHead from '../../components/SEOHead';
import { adminService } from '../../services/adminService';
import { EmailConfig } from '../../types/admin';
import { generateCanonicalUrl } from '../../utils/seoUtils';

const AdminEmailSettings: React.FC = () => {
  const [emailConfig, setEmailConfig] = useState<EmailConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isTesting, setIsTesting] = useState(false);

  const [settings, setSettings] = useState({
    orderCreatedNotifications: true,
    orderStatusNotifications: true,
    lowStockNotifications: true,
    customerServiceNotifications: true,
    adminEmail: '',
  });

  useEffect(() => {
    const load = async () => {
      await validateEmailConfiguration();
      try {
        const s = await adminService.getEmailSettings();
        setSettings({
          adminEmail: s.adminEmail || '',
          orderCreatedNotifications: s.orderCreatedNotifications ?? true,
          orderStatusNotifications: s.orderStatusNotifications ?? true,
          lowStockNotifications: s.lowStockNotifications ?? true,
          customerServiceNotifications: s.customerServiceNotifications ?? true,
        });
      } catch (e) {
        // keep defaults
      }
    };
    load();
  }, []);

  const validateEmailConfiguration = async () => {
    try {
      setIsLoading(true);
      const response = await adminService.validateEmailConfiguration();
      setEmailConfig(response);
    } catch (error: any) {
      setEmailConfig({
        isValid: false,
        message: 'Failed to validate email configuration',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const testEmailConfiguration = async () => {
    try {
      setIsTesting(true);
      const result = await adminService.sendTestEmail(
        settings.adminEmail || undefined
      );
      toast.success(
        result?.message || 'Test email sent successfully! Check your inbox.'
      );
    } catch (error: any) {
      toast.error(
        'Failed to send test email. Please check your configuration.'
      );
    } finally {
      setIsTesting(false);
    }
  };

  const handleSettingChange = (key: string, value: boolean | string) => {
    setSettings(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const saveSettings = async () => {
    try {
      await adminService.saveEmailSettings(settings);
      toast.success('Email settings saved successfully');
    } catch (error: any) {
      toast.error('Failed to save email settings');
    }
  };

  if (isLoading) {
    return (
      <div className='space-y-6'>
        <div className='animate-pulse'>
          <div className='h-8 bg-gray-300 rounded w-1/4 mb-6' />
          <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
            <div className='h-64 bg-gray-300 rounded-lg' />
            <div className='h-64 bg-gray-300 rounded-lg' />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <SEOHead
        title='Admin Email Settings'
        description='Configure email notifications and SMTP settings for the store.'
        url={generateCanonicalUrl('/admin/email-settings')}
        type='website'
      />
      {/* Header */}
      <div className='md:flex md:items-center md:justify-between'>
        <div className='flex-1 min-w-0'>
          <h2 className='text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate'>
            Email Settings
          </h2>
          <p className='mt-1 text-sm text-gray-500'>
            Configure email notifications and SMTP settings
          </p>
        </div>
        <div className='mt-4 flex md:mt-0 md:ml-4'>
          <Button color='primary' onClick={saveSettings}>
            Save Settings
          </Button>
        </div>
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
        {/* Email Configuration Status */}
        <Card>
          <CardHeader>
            <div className='flex items-center gap-2'>
              <Cog6ToothIcon className='h-5 w-5 text-gray-500' />
              <span className='font-medium'>Email Configuration Status</span>
            </div>
          </CardHeader>
          <CardBody>
            <div className='space-y-4'>
              <div className='flex items-center justify-between p-4 border rounded-lg'>
                <div className='flex items-center space-x-3'>
                  {emailConfig?.isValid ? (
                    <CheckCircleIcon className='h-6 w-6 text-success-500' />
                  ) : (
                    <ExclamationTriangleIcon className='h-6 w-6 text-warning-500' />
                  )}
                  <div>
                    <h3 className='font-medium'>SMTP Configuration</h3>
                    <p className='text-sm text-gray-600'>
                      {emailConfig?.message}
                    </p>
                  </div>
                </div>
                <Chip
                  color={emailConfig?.isValid ? 'success' : 'warning'}
                  variant='flat'
                >
                  {emailConfig?.isValid ? 'Valid' : 'Invalid'}
                </Chip>
              </div>

              <div className='space-y-2'>
                <Button
                  fullWidth
                  color='secondary'
                  variant='flat'
                  onClick={validateEmailConfiguration}
                  isLoading={isLoading}
                >
                  Validate Configuration
                </Button>
                <Button
                  fullWidth
                  color='primary'
                  variant='flat'
                  onClick={testEmailConfiguration}
                  isLoading={isTesting}
                  isDisabled={!emailConfig?.isValid}
                >
                  Send Test Email
                </Button>
              </div>

              {!emailConfig?.isValid && (
                <div className='p-4 bg-warning-50 border border-warning-200 rounded-lg'>
                  <h4 className='font-medium text-warning-800 mb-2'>
                    Configuration Required
                  </h4>
                  <p className='text-sm text-warning-700 mb-3'>
                    Email notifications are disabled because SMTP is not
                    configured. Please update your appsettings.json with the
                    following:
                  </p>
                  <pre className='text-xs bg-gray-800 text-gray-100 p-3 rounded overflow-x-auto'>
                    {`"Email": {
  "SmtpServer": "smtp.gmail.com",
  "SmtpPort": 587,
  "Username": "your-email@gmail.com",
  "Password": "your-app-password",
  "FromEmail": "your-email@gmail.com",
  "FromName": "CodedGhost Minis",
  "AdminEmail": "admin@example.com"
}`}
                  </pre>
                </div>
              )}
            </div>
          </CardBody>
        </Card>

        {/* Notification Settings */}
        <Card>
          <CardHeader>
            <div className='flex items-center gap-2'>
              <EnvelopeIcon className='h-5 w-5 text-gray-500' />
              <span className='font-medium'>Notification Settings</span>
            </div>
          </CardHeader>
          <CardBody>
            <div className='space-y-6'>
              <div>
                <Input
                  label='Admin Email Address'
                  placeholder='admin@example.com'
                  value={settings.adminEmail}
                  onChange={e =>
                    handleSettingChange('adminEmail', e.target.value)
                  }
                  description='Email address to receive admin notifications'
                />
              </div>

              <Divider />

              <div className='space-y-4'>
                <h4 className='font-medium text-gray-900'>
                  Email Notifications
                </h4>

                <div className='flex items-center justify-between'>
                  <div>
                    <h5 className='font-medium'>Order Created</h5>
                    <p className='text-sm text-gray-600'>
                      Get notified when new orders are placed
                    </p>
                  </div>
                  <Switch
                    isSelected={settings.orderCreatedNotifications}
                    onValueChange={value =>
                      handleSettingChange('orderCreatedNotifications', value)
                    }
                  />
                </div>

                <div className='flex items-center justify-between'>
                  <div>
                    <h5 className='font-medium'>Order Status Updates</h5>
                    <p className='text-sm text-gray-600'>
                      Send emails to customers when order status changes
                    </p>
                  </div>
                  <Switch
                    isSelected={settings.orderStatusNotifications}
                    onValueChange={value =>
                      handleSettingChange('orderStatusNotifications', value)
                    }
                  />
                </div>

                <div className='flex items-center justify-between'>
                  <div>
                    <h5 className='font-medium'>Low Stock Alerts</h5>
                    <p className='text-sm text-gray-600'>
                      Get notified when product inventory is low
                    </p>
                  </div>
                  <Switch
                    isSelected={settings.lowStockNotifications}
                    onValueChange={value =>
                      handleSettingChange('lowStockNotifications', value)
                    }
                  />
                </div>

                <div className='flex items-center justify-between'>
                  <div>
                    <h5 className='font-medium'>Customer Service</h5>
                    <p className='text-sm text-gray-600'>
                      Get notified about new support tickets
                    </p>
                  </div>
                  <Switch
                    isSelected={settings.customerServiceNotifications}
                    onValueChange={value =>
                      handleSettingChange('customerServiceNotifications', value)
                    }
                  />
                </div>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Email Templates */}
      <Card>
        <CardHeader>
          <div className='flex items-center justify-between'>
            <h3 className='text-lg font-medium'>Email Templates</h3>
            <Button
              color='primary'
              variant='ghost'
              size='sm'
              onClick={async () => {
                // simple flow: ensure base templates exist
                const templates = await adminService.getEmailTemplates();
                if (!templates || templates.length === 0) {
                  await adminService.upsertEmailTemplate({
                    templateType: 'OrderConfirmation',
                    subject: 'Order Confirmation - {{orderNumber}}',
                    htmlBody:
                      '<h2>Thank you for your order</h2><p>Order {{orderNumber}} placed on {{orderDate}}.</p>',
                  });
                  await adminService.upsertEmailTemplate({
                    templateType: 'StatusUpdate',
                    subject:
                      'Your order {{orderNumber}} status is now {{status}}',
                    htmlBody:
                      '<p>Your order {{orderNumber}} status changed to {{status}}.</p>',
                  });
                  await adminService.upsertEmailTemplate({
                    templateType: 'AdminAlert',
                    subject: 'New order received - {{orderNumber}}',
                    htmlBody:
                      '<p>A new order {{orderNumber}} has been placed.</p>',
                  });
                  toast.success('Default templates created');
                } else {
                  toast.success('Templates loaded');
                }
              }}
            >
              Ensure Default Templates
            </Button>
          </div>
        </CardHeader>
        <CardBody>
          <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
            <div className='p-4 border rounded-lg'>
              <h4 className='font-medium mb-2'>Order Confirmation</h4>
              <p className='text-sm text-gray-600 mb-3'>
                Sent to customers when an order is placed
              </p>
              <div className='flex gap-2'>
                <Button
                  size='sm'
                  variant='ghost'
                  fullWidth
                  onClick={async () => {
                    const html = await adminService.previewEmailTemplate(
                      'OrderConfirmation',
                      {
                        orderNumber: 'CG20250101-0001',
                        orderDate: new Date().toLocaleDateString(),
                      }
                    );
                    const w = window.open('', '_blank');
                    if (w) w.document.write(html);
                  }}
                >
                  Preview
                </Button>
                <Button
                  size='sm'
                  variant='bordered'
                  fullWidth
                  onClick={async () => {
                    const t =
                      await adminService.getEmailTemplate('OrderConfirmation');
                    const subject = prompt('Subject', t?.subject || '');
                    if (subject === null) return;
                    const body = prompt('HTML Body', t?.htmlBody || '');
                    if (body === null) return;
                    await adminService.upsertEmailTemplate({
                      templateType: 'OrderConfirmation',
                      subject,
                      htmlBody: body,
                    });
                    toast.success('Template saved');
                  }}
                >
                  Edit
                </Button>
              </div>
            </div>

            <div className='p-4 border rounded-lg'>
              <h4 className='font-medium mb-2'>Status Update</h4>
              <p className='text-sm text-gray-600 mb-3'>
                Sent when order status changes
              </p>
              <div className='flex gap-2'>
                <Button
                  size='sm'
                  variant='ghost'
                  fullWidth
                  onClick={async () => {
                    const html = await adminService.previewEmailTemplate(
                      'StatusUpdate',
                      {
                        orderNumber: 'CG20250101-0001',
                        status: 'Processing',
                      }
                    );
                    const w = window.open('', '_blank');
                    if (w) w.document.write(html);
                  }}
                >
                  Preview
                </Button>
                <Button
                  size='sm'
                  variant='bordered'
                  fullWidth
                  onClick={async () => {
                    const t =
                      await adminService.getEmailTemplate('StatusUpdate');
                    const subject = prompt('Subject', t?.subject || '');
                    if (subject === null) return;
                    const body = prompt('HTML Body', t?.htmlBody || '');
                    if (body === null) return;
                    await adminService.upsertEmailTemplate({
                      templateType: 'StatusUpdate',
                      subject,
                      htmlBody: body,
                    });
                    toast.success('Template saved');
                  }}
                >
                  Edit
                </Button>
              </div>
            </div>

            <div className='p-4 border rounded-lg'>
              <h4 className='font-medium mb-2'>Admin Alert</h4>
              <p className='text-sm text-gray-600 mb-3'>
                Sent to admin for new orders
              </p>
              <div className='flex gap-2'>
                <Button
                  size='sm'
                  variant='ghost'
                  fullWidth
                  onClick={async () => {
                    const html = await adminService.previewEmailTemplate(
                      'AdminAlert',
                      {
                        orderNumber: 'CG20250101-0001',
                      }
                    );
                    const w = window.open('', '_blank');
                    if (w) w.document.write(html);
                  }}
                >
                  Preview
                </Button>
                <Button
                  size='sm'
                  variant='bordered'
                  fullWidth
                  onClick={async () => {
                    const t = await adminService.getEmailTemplate('AdminAlert');
                    const subject = prompt('Subject', t?.subject || '');
                    if (subject === null) return;
                    const body = prompt('HTML Body', t?.htmlBody || '');
                    if (body === null) return;
                    await adminService.upsertEmailTemplate({
                      templateType: 'AdminAlert',
                      subject,
                      htmlBody: body,
                    });
                    toast.success('Template saved');
                  }}
                >
                  Edit
                </Button>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default AdminEmailSettings;
